import React, { useId } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { AVATAR_PLACEHOLDER } from '../lib/sampleResume';

const TEMPLATE_CONFIG = {
  indian: { accent: '#B45309', sidebar: false, headerStyle: 'indian', uppercaseHeads: true, photoHeader: true },
  modern: { accent: '#0A0F1C', sidebar: false, headerStyle: 'bar', uppercaseHeads: true },
  classic: { accent: '#1E293B', sidebar: false, headerStyle: 'center', uppercaseHeads: false },
  minimal: { accent: '#0F766E', sidebar: false, headerStyle: 'plain', uppercaseHeads: true },
  professional: { accent: '#C2410C', sidebar: false, headerStyle: 'band', uppercaseHeads: true },
  elegant: { accent: '#6D28D9', sidebar: true, headerStyle: 'sidebar', uppercaseHeads: true },
  compact: { accent: '#0369A1', sidebar: false, headerStyle: 'compact', uppercaseHeads: true },
};

const isEmptyArr = (a) => !a || a.length === 0;

export const ResumePreview = ({ resume, scale = 1, testIdPrefix }) => {
  const instance = useId().replace(/:/g, '');
  const prefix = testIdPrefix || `resume-${instance}`;
  const { t, dir } = useLanguage();
  const cfg = TEMPLATE_CONFIG[resume.template_id] || TEMPLATE_CONFIG.modern;
  const s = resume.sections || {};
  const contact = s.contact || {};
  const india = resume.india_fields || {};
  const accent = resume.theme_color || cfg.accent;

  const SectionTitle = ({ children }) => (
    <h3
      className="mb-2 mt-4 border-b pb-1 font-heading text-[13px] font-bold"
      style={{ color: accent, borderColor: `${accent}33`, textTransform: cfg.uppercaseHeads ? 'uppercase' : 'none', letterSpacing: cfg.uppercaseHeads ? '0.08em' : '0' }}
    >
      {children}
    </h3>
  );

  const Summary = () => s.summary ? (
    <div data-testid={`${prefix}-summary`}>
      <SectionTitle>{t('section.summary')}</SectionTitle>
      <p className="text-[12px] leading-relaxed text-gray-700">{s.summary}</p>
    </div>
  ) : null;

  const Experience = () => !isEmptyArr(s.experience) ? (
    <div data-testid={`${prefix}-experience`}>
      <SectionTitle>{t('section.experience')}</SectionTitle>
      {s.experience.map((e, i) => (
        <div key={e.id || i} className="mb-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-semibold text-gray-900">{e.role || ''}</span>
            <span className="whitespace-nowrap text-[11px] text-gray-500">{[e.startDate, e.endDate || (e.startDate ? t('common.present') : '')].filter(Boolean).join(' — ')}</span>
          </div>
          <div className="text-[11.5px] font-medium" style={{ color: accent }}>{e.company || ''}</div>
          {Array.isArray(e.bullets) && e.bullets.length > 0 ? (
            <ul className="mt-1 list-disc space-y-0.5 ps-4 text-[11.5px] leading-relaxed text-gray-700">
              {e.bullets.filter(Boolean).map((b, j) => <li key={j}>{b}</li>)}
            </ul>
          ) : e.description ? (
            <p className="mt-1 text-[11.5px] leading-relaxed text-gray-700">{e.description}</p>
          ) : null}
        </div>
      ))}
    </div>
  ) : null;

  const Education = () => !isEmptyArr(s.education) ? (
    <div data-testid={`${prefix}-education`}>
      <SectionTitle>{t('section.education')}</SectionTitle>
      {s.education.map((e, i) => (
        <div key={e.id || i} className="mb-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-semibold text-gray-900">{e.degree || ''}</span>
            <span className="whitespace-nowrap text-[11px] text-gray-500">{[e.startDate, e.endDate].filter(Boolean).join(' — ')}</span>
          </div>
          <div className="text-[11.5px] text-gray-600">{e.institution || ''}{e.grade ? ` · ${e.grade}` : ''}</div>
        </div>
      ))}
    </div>
  ) : null;

  const Skills = () => !isEmptyArr(s.skills) ? (
    <div data-testid={`${prefix}-skills`}>
      <SectionTitle>{t('section.skills')}</SectionTitle>
      <div className="flex flex-wrap gap-1.5">
        {s.skills.filter(Boolean).map((sk, i) => (
          <span key={i} className="rounded-sm px-2 py-0.5 text-[11px]" style={{ background: `${accent}14`, color: accent }}>{sk}</span>
        ))}
      </div>
    </div>
  ) : null;

  const Projects = () => !isEmptyArr(s.projects) ? (
    <div data-testid={`${prefix}-projects`}>
      <SectionTitle>{t('section.projects')}</SectionTitle>
      {s.projects.map((p, i) => (
        <div key={p.id || i} className="mb-2">
          <span className="text-[12.5px] font-semibold text-gray-900">{p.name || ''}</span>
          {p.link ? <span className="ms-2 text-[11px]" style={{ color: accent }}>{p.link}</span> : null}
          {p.description ? <p className="text-[11.5px] leading-relaxed text-gray-700">{p.description}</p> : null}
        </div>
      ))}
    </div>
  ) : null;

  const Certifications = () => !isEmptyArr(s.certifications) ? (
    <div data-testid={`${prefix}-certifications`}>
      <SectionTitle>{t('section.certifications')}</SectionTitle>
      {s.certifications.map((c, i) => (
        <div key={c.id || i} className="mb-1 text-[11.5px] text-gray-700">
          <span className="font-semibold text-gray-900">{c.name || ''}</span>{c.issuer ? ` — ${c.issuer}` : ''}{c.date ? ` (${c.date})` : ''}
        </div>
      ))}
    </div>
  ) : null;

  const Languages = () => !isEmptyArr(s.languages) ? (
    <div data-testid={`${prefix}-languages`}>
      <SectionTitle>{t('section.languages')}</SectionTitle>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-gray-700">
        {s.languages.filter((l) => l.language).map((l, i) => (
          <span key={l.id || i}><span className="font-medium text-gray-900">{l.language}</span>{l.proficiency ? ` · ${l.proficiency}` : ''}</span>
        ))}
      </div>
    </div>
  ) : null;

  const Custom = () => !isEmptyArr(s.custom) ? (
    <>
      {s.custom.filter((c) => c.title || c.content).map((c, i) => (
        <div key={c.id || i} data-testid={`${prefix}-custom-${i}`}>
          <SectionTitle>{c.title || ''}</SectionTitle>
          <p className="whitespace-pre-line text-[11.5px] leading-relaxed text-gray-700">{c.content || ''}</p>
        </div>
      ))}
    </>
  ) : null;

  const declaration = india.enabled && india.declaration ? (
    <div className="mt-4 border-t pt-2 text-[11px] italic text-gray-600" style={{ borderColor: `${accent}22` }}>
      {india.declaration}
    </div>
  ) : null;

  // Header variants
  const ContactLine = () => (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
      {[contact.email, contact.phone, contact.location, contact.website].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
    </div>
  );

  const photoEl = india.enabled && india.photo ? (
    <img src={india.photo} alt="" className="h-20 w-16 rounded-sm border object-cover" style={{ borderColor: `${accent}33` }} />
  ) : null;
  // Indian template always shows a photo area (placeholder until the user uploads one).
  const indianPhoto = india.photo || (cfg.photoHeader ? AVATAR_PLACEHOLDER : null);

  let header;
  if (cfg.headerStyle === 'indian') {
    header = (
      <div className="mb-3 overflow-hidden rounded-sm border" style={{ borderColor: `${accent}33` }}>
        <div className="h-2" style={{ background: accent }} />
        <div className="flex items-center gap-4 px-4 py-3">
          {indianPhoto && <img src={indianPhoto} alt="" className="h-24 w-20 shrink-0 rounded-sm border object-cover" style={{ borderColor: `${accent}44` }} />}
          <div className="flex-1">
            <p role="heading" aria-level="2" className="font-heading text-[23px] font-bold tracking-tight text-gray-900">{contact.fullName || t('common.untitled')}</p>
            <div className="text-[13px] font-medium" style={{ color: accent }}>{contact.jobTitle || ''}</div>
            <ContactLine />
            {india.enabled && india.dob ? <div className="mt-0.5 text-[11px] text-gray-600">{t('field.dob')}: {india.dob}</div> : null}
          </div>
        </div>
      </div>
    );
  } else if (cfg.headerStyle === 'band') {
    header = (
      <div className="mb-3 flex items-center justify-between gap-3 rounded-sm px-4 py-3 text-white" style={{ background: accent }}>
        <div>
          <p role="heading" aria-level="2" className="font-heading text-[22px] font-bold leading-tight">{contact.fullName || t('common.untitled')}</p>
          <div className="text-[13px] opacity-90">{contact.jobTitle || ''}</div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-[10.5px] opacity-90">
            {[contact.email, contact.phone, contact.location, contact.website].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
          </div>
        </div>
        {photoEl}
      </div>
    );
  } else if (cfg.headerStyle === 'center') {
    header = (
      <div className="mb-3 flex flex-col items-center text-center">
        {photoEl}
        <p role="heading" aria-level="2" className="font-heading text-[24px] font-bold tracking-tight text-gray-900">{contact.fullName || t('common.untitled')}</p>
        <div className="text-[13px] font-medium" style={{ color: accent }}>{contact.jobTitle || ''}</div>
        <div className="mt-1 flex flex-wrap justify-center gap-x-3 text-[11px] text-gray-600">
          {[contact.email, contact.phone, contact.location, contact.website].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
        </div>
      </div>
    );
  } else if (cfg.headerStyle === 'bar') {
    header = (
      <div className="mb-3 flex items-center gap-3 border-s-4 ps-3" style={{ borderColor: accent }}>
        {photoEl}
        <div>
          <p role="heading" aria-level="2" className="font-heading text-[23px] font-bold tracking-tight text-gray-900">{contact.fullName || t('common.untitled')}</p>
          <div className="text-[13px] font-medium" style={{ color: accent }}>{contact.jobTitle || ''}</div>
          <ContactLine />
        </div>
      </div>
    );
  } else {
    header = (
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p role="heading" aria-level="2" className="font-heading text-[22px] font-bold tracking-tight text-gray-900">{contact.fullName || t('common.untitled')}</p>
          <div className="text-[13px] font-medium" style={{ color: accent }}>{contact.jobTitle || ''}</div>
          <ContactLine />
        </div>
        {photoEl}
      </div>
    );
  }

  if (cfg.sidebar) {
    return (
      <div data-testid={`${prefix}-canvas`} dir={dir} className="mx-auto bg-white text-gray-900" style={{ width: '794px', minHeight: '1123px', transform: `scale(${scale})`, transformOrigin: 'top center', fontFamily: 'inherit' }}>
        <div className="flex min-h-[1123px]">
          <aside className="w-[34%] p-5 text-white" style={{ background: accent }}>
            {photoEl && <div className="mb-3">{photoEl}</div>}
            <p role="heading" aria-level="2" className="font-heading text-[20px] font-bold leading-tight">{contact.fullName || t('common.untitled')}</p>
            <div className="text-[12px] opacity-90">{contact.jobTitle || ''}</div>
            <div className="mt-3 space-y-1 text-[10.5px] opacity-90">
              {[contact.email, contact.phone, contact.location, contact.website].filter(Boolean).map((x, i) => <div key={i}>{x}</div>)}
            </div>
            {!isEmptyArr(s.skills) && (
              <div className="mt-4">
                <h3 className="mb-1 text-[12px] font-bold uppercase tracking-wide">{t('section.skills')}</h3>
                <div className="space-y-1 text-[11px] opacity-90">{s.skills.filter(Boolean).map((sk, i) => <div key={i}>{sk}</div>)}</div>
              </div>
            )}
            {!isEmptyArr(s.languages) && (
              <div className="mt-4">
                <h3 className="mb-1 text-[12px] font-bold uppercase tracking-wide">{t('section.languages')}</h3>
                <div className="space-y-1 text-[11px] opacity-90">{s.languages.filter((l) => l.language).map((l, i) => <div key={i}>{l.language}{l.proficiency ? ` · ${l.proficiency}` : ''}</div>)}</div>
              </div>
            )}
          </aside>
          <main className="w-[66%] p-6">
            <Summary /><Experience /><Education /><Projects /><Certifications /><Custom />
            {declaration}
          </main>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${prefix}-canvas`} dir={dir} className="mx-auto bg-white p-7 text-gray-900" style={{ width: '794px', minHeight: '1123px', transform: `scale(${scale})`, transformOrigin: 'top center', fontFamily: 'inherit' }}>
      {header}
      <Summary />
      <Experience />
      <Education />
      <Skills />
      <Projects />
      <Certifications />
      <Languages />
      <Custom />
      {declaration}
    </div>
  );
};
